import { Suspense, useEffect, useState, type ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { AuthGuard } from '@shared/components/layout/AuthGuard'
import { AdminShell } from '@shared/components/layout/AdminShell'
import { LoginPage } from '@shared/components/layout/LoginPage'
import { api } from '@shared/lib/api-client'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import HomePage from './pages/HomePage'
import { ALL_ROUTES, REDIRECTS, findActive } from './nav'

function Loading() {
  return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>
}

interface SessionData {
  user: { id: string; email: string; name: string; role: string }
  permissions: string[]
  schools: Array<{ id: string; name: string; slug: string }>
  defaultSchoolId: string | null
}

function SessionBootstrap({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<{ data: SessionData }>('/api/admin/session').then(({ data }) => {
      localStorage.setItem('fc_admin_auth', JSON.stringify({ ...data.user, permissions: data.permissions }))
      // Resolve the active workspace. Keep the stored one if still valid, else default
      // to Conan School so uploads/content don't land in another tenant (e.g. Chuyên Chọn).
      // Persist id + name (for read-only display in cluster shells) and the full list
      // (so the HOME page can offer the school picker without another API call).
      const current = localStorage.getItem('fc_school_id')
      const chosen = (current && data.schools.find((school) => school.id === current))
        || data.schools.find((school) => school.slug === 'conan-school')
        || data.schools.find((school) => school.id === data.defaultSchoolId)
        || data.schools[0]
      if (chosen) {
        localStorage.setItem('fc_school_id', chosen.id)
        localStorage.setItem('fc_school_name', chosen.name)
      } else {
        localStorage.removeItem('fc_school_id')
        localStorage.removeItem('fc_school_name')
      }
      localStorage.setItem('fc_schools', JSON.stringify(data.schools))
      setReady(true)
    }).catch((cause) => setError(cause instanceof Error ? cause.message : 'Không thể tải phiên quản trị'))
  }, [])

  if (error) return <div className="min-h-screen grid place-items-center p-6"><div className="max-w-lg rounded-xl border p-6"><h1 className="font-bold text-lg">Không có quyền truy cập</h1><p className="text-sm text-muted-foreground mt-2">{error}</p></div></div>
  return ready ? <>{children}</> : <Loading />
}

/**
 * Layout for every cluster route: resolves the active group/cluster from the URL
 * and renders the lean sidebar shell around the matched page.
 */
function ClusterLayout() {
  const { pathname } = useLocation()
  const active = findActive(pathname)
  if (!active) return <Navigate to="/" replace />
  const { group, cluster } = active
  return (
    <AdminShell
      groupLabel={group.label}
      clusterLabel={cluster.label}
      items={cluster.items}
      siblings={group.clusters.map(c => ({ id: c.id, label: c.label, basePath: c.basePath }))}
    >
      <Suspense fallback={<Loading />}><Outlet /></Suspense>
    </AdminShell>
  )
}

function ProtectedPortal() {
  return (
    <SessionBootstrap>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route element={<ClusterLayout />}>
          {ALL_ROUTES.map(route => <Route key={route.path} path={route.path} element={<route.Component />} />)}
        </Route>
        {REDIRECTS.map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionBootstrap>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage appName="Conan Admin" appIcon={<Cog6ToothIcon className="size-8" />} basePath="/" />} />
      <Route path="/*" element={<AuthGuard loginPath="/login"><ProtectedPortal /></AuthGuard>} />
    </Routes>
  )
}
